import { useState, useEffect, useCallback } from 'react';
import { humanFeedbackService } from '../apis/humanFeedbackService';

export const useFeedbackState = (soapNoteId) => {
  const [feedbackState, setFeedbackState] = useState({
    sections: {
      subjective: null,
      objective: null,
      assessmentAndPlan: null
    },
    paragraphLevelFeedbacks: [],
    sectionVersions: {},
    feedbackHistory: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadFeedbackState = useCallback(async () => {
    if (!soapNoteId || soapNoteId === "demo-soap-note") {
      setFeedbackState({
        sections: {
          subjective: null,
          objective: null,
          assessmentAndPlan: null
        },
        paragraphLevelFeedbacks: [],
        sectionVersions: {},
        feedbackHistory: []
      });
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setFeedbackState({
        sections: {
          subjective: null,
          objective: null,
          assessmentAndPlan: null
        },
        paragraphLevelFeedbacks: [],
        sectionVersions: {},
        feedbackHistory: []
      });
      const response = await humanFeedbackService.getUserFeedbackState(soapNoteId);
      setFeedbackState(response.data.data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading feedback state:', err);
    } finally {
      setLoading(false);
    }
  }, [soapNoteId]);

  const updateFeedbackState = (section, type, feedbackType = 'global-level', reportedContent = '') => {
    const feedbackItem = {
      type: type === 'like' ? 'thumbs_up' : 'thumbs_down',
      feedbackType,
      reportedContent,
      createdAt: new Date().toISOString()
    };

    setFeedbackState(prev => {
      const newState = { ...prev };
      
      if (feedbackType === 'paragraph-level') {
        newState.paragraphLevelFeedbacks = [...prev.paragraphLevelFeedbacks, feedbackItem];
      } else {
        newState.sections[section] = feedbackItem;
      }
      
      return newState;
    });
  };

  const getSectionFeedbackState = (section) => {
    const feedback = feedbackState.sections[section];
    if (!feedback) return null;
    
    return feedback.type === 'thumbs_up' ? 'like' : 'dislike';
  };

  const getParagraphFeedbackState = (content) => {
    if (!content) return null;
    
    const normalizeContent = (text) => {
      return text.trim().replace(/\s+/g, ' ').replace(/\n+/g, ' ').toLowerCase();
    };
    
    const normalizedContent = normalizeContent(content);
    
    const feedback = feedbackState.paragraphLevelFeedbacks.find(f => {
      if (f.feedbackType !== 'paragraph-level') return false;
      if (!f.reportedContent) return false;
      
      const normalizedReportedContent = normalizeContent(f.reportedContent);
      return normalizedReportedContent === normalizedContent;
    });
    
    
    if (!feedback) return null;
    return feedback.type === 'thumbs_up' ? 'like' : 'dislike';
  };

  useEffect(() => {
    loadFeedbackState();
  }, [soapNoteId, loadFeedbackState]);

  const getParagraphFeedbackDebug = (content) => {
    if (!content) return { content, feedback: null, allFeedback: feedbackState.paragraphLevelFeedbacks };
    
    const normalizeContent = (text) => {
      return text.trim().replace(/\s+/g, ' ').replace(/\n+/g, ' ').toLowerCase();
    };
    
    const normalizedContent = normalizeContent(content);
    
    const feedback = feedbackState.paragraphLevelFeedbacks.find(f => {
      if (f.feedbackType !== 'paragraph-level') return false;
      if (!f.reportedContent) return false;
      
      const normalizedReportedContent = normalizeContent(f.reportedContent);
      return normalizedReportedContent === normalizedContent;
    });
    
    return {
      content,
      normalizedContent,
      feedback,
      allFeedback: feedbackState.paragraphLevelFeedbacks.map(f => ({
        ...f,
        normalizedReportedContent: f.reportedContent ? normalizeContent(f.reportedContent) : null
      }))
    };
  };

  const getSectionFeedbackHistory = useCallback(async (section, version = null) => {
    if (!soapNoteId || !section) return null;
    try {
      if (section === 'assessmentAndPlan') {
        const [assessmentHistory, planHistory] = await Promise.all([
          humanFeedbackService.getSectionFeedbackHistory(soapNoteId, 'assessment', version),
          humanFeedbackService.getSectionFeedbackHistory(soapNoteId, 'plan', version)
        ]);
        
        const combinedHistory = {
          section: 'assessmentAndPlan',
          currentVersion: Math.max(
            assessmentHistory.data.data.currentVersion || 1,
            planHistory.data.data.currentVersion || 1
          ),
          requestedVersion: version,
          feedbackHistory: [
            ...(assessmentHistory.data.data.feedbackHistory || []).map(f => ({
              ...f,
              section: 'assessment'
            })),
            ...(planHistory.data.data.feedbackHistory || []).map(f => ({
              ...f,
              section: 'plan'
            }))
          ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        };
        
        return combinedHistory;
      }
      
      const response = await humanFeedbackService.getSectionFeedbackHistory(soapNoteId, section, version);
      return response.data.data;
    } catch (err) {
      console.error('Error loading section feedback history:', err);
      return null;
    }
  }, [soapNoteId]);

  const getCurrentSectionVersion = (section) => {
    if (section === 'assessmentAndPlan') {
      const assessmentVersion = feedbackState.sectionVersions['assessment']?.version || 1;
      const planVersion = feedbackState.sectionVersions['plan']?.version || 1;
      const maxVersion = Math.max(assessmentVersion, planVersion);
      return maxVersion;
    }
    const version = feedbackState.sectionVersions[section]?.version || 1;
    return version;
  };

  const hasSectionBeenEdited = (section) => {
    if (section === 'assessmentAndPlan') {
      const assessmentVersion = feedbackState.sectionVersions['assessment']?.version || 1;
      const planVersion = feedbackState.sectionVersions['plan']?.version || 1;
      const hasBeenEdited = assessmentVersion > 1 || planVersion > 1;
      return hasBeenEdited;
    }
    const version = getCurrentSectionVersion(section);
    const hasBeenEdited = version > 1;
    return hasBeenEdited;
  };

  return {
    feedbackState,
    loading,
    error,
    loadFeedbackState,
    updateFeedbackState,
    getSectionFeedbackState,
    getParagraphFeedbackState,
    getParagraphFeedbackDebug,
    getSectionFeedbackHistory,
    getCurrentSectionVersion,
    hasSectionBeenEdited
  };
};
